import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { red, orange, green } from '@mui/material/colors';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const getColumns = ({
  handleItemOpenModal,
  handleLastOrderedClick,
  onOpenHelp,
  theme,
  userPersonalization,
}) => {
  const openColumnHelp = (columnName) => {
    onOpenHelp?.({
      faqDescription: 'What do all the columns mean',
      sectionTitle: 'Smart Order Tab',
      columnName,
    });
  };

  const renderHelpHeader = (label, columnName) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.35,
        minWidth: 0,
        pr: 2.5,
      }}
    >
      <Tooltip title='What does this mean?'>
        <IconButton
          size='small'
          aria-label={`What does ${label} mean?`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openColumnHelp(columnName);
          }}
          sx={{ p: 0.25 }}
        >
          <HelpOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
      <Typography variant='body2' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  );

  const getPersonalizedValue = (key) => {
    const match = userPersonalization?.find((p) => p.personalized_element === key);
    return match?.personalized_value;
  };

  const yourQtyHeader = getPersonalizedValue('YourQtyHeader') || 'In Store';

  return [
    {
      field: 'dealer_quantity',
      headerName: yourQtyHeader,
      renderHeader: () => renderHelpHeader(yourQtyHeader, 'Your Qty'),
      width: 110,
      renderCell: (params) => {
        let textColor;
        let backgroundColor;
        if (params.value === 0) {
          textColor = red[700];
          backgroundColor = red[100];
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              backgroundColor,
              width: '100%',
              height: '100%',
            }}
          >
            <Typography variant='body1' sx={{ fontWeight: 'bold', color: textColor }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'last_ordered_days',
      headerName: 'Last Ordered',
      renderHeader: () => renderHelpHeader('Last Ordered', 'Last Ordered'),
      width: 160,
      sortComparator: (a, b) => {
        if (a === null && b === null) return 0;
        if (a === null) return 1;
        if (b === null) return -1;
        return a - b;
      },
      renderCell: (params) => {
        let textColor;
        let backgroundColor;
        let displayValue;

        if (params.value === null || params.value === undefined) {
          displayValue = 'No Sales Data';
          textColor = 'inherit';
        } else if (params.value === 'N/A') {
          displayValue = 'N/A';
          textColor = 'inherit';
        } else if (params.value === 0) {
          displayValue = 'Ordered Today';
          textColor = red[700];
          backgroundColor = red[100];
        } else if (params.value === 1) {
          displayValue = '1 Day Ago';
          textColor = red[700];
          backgroundColor = red[100];
        } else {
          displayValue = `${params.value} Days Ago`;
          if (params.value <= 3) {
            textColor = red[700];
            backgroundColor = red[100];
          } else if (params.value >= 4 && params.value <= 11) {
            textColor = orange[700];
            backgroundColor = orange[100];
          } else {
            textColor = 'inherit';
          }
        }
        return (
          <Box
            onClick={() => handleLastOrderedClick?.(params.row.item_number)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor,
              width: '100%',
              height: '100%',
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            <Typography
              variant='body1'
              sx={{
                fontWeight: 'bold',
                color: textColor || '#1976d2',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                textDecorationThickness: '1px',
                transition: 'filter 120ms ease',
                '&:hover': {
                  filter: 'brightness(0.85)',
                },
              }}
            >
              {displayValue}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'qty_sold_last_30_days',
      headerName: 'YTD Sold',
      renderHeader: () => renderHelpHeader('YTD Sold', 'YTD Sold'),
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='body1'>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'quantity_on_hand',
      headerName: 'SS Qty',
      renderHeader: () => renderHelpHeader('SS Qty', 'SS Qty'),
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='body1'>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      renderHeader: () => renderHelpHeader('Category', 'Category'),
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='body1'>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      renderHeader: () => renderHelpHeader('Description', 'Description'),
      minWidth: 320,
      flex: 1,
      renderCell: (params) => {
        const itemNumber = params.row.item_number;
        const upc = params.row.upc;
        const itemLabel = itemNumber ?? 'N/A';
        const upcLabel = upc ?? 'N/A';
        const isDiscontinued =
          params.row.discontinued === true ||
          String(params.row.discontinued || '').trim().toLowerCase() === 'true';

        return (
          <Box
            onClick={() => handleItemOpenModal(itemNumber)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              cursor: 'pointer',
            }}
          >
            <Typography variant='body1' sx={{ color: '#1976d2', textDecoration: 'underline' }}>
              {params.value}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography variant='body2' color='text.secondary'>
                {`Item #: ${itemLabel}`}
              </Typography>
              {isDiscontinued ? (
                <Typography
                  variant='body2'
                  sx={{ color: theme.palette.secondary.main, fontWeight: 700 }}
                >
                  DISCONTINUED
                </Typography>
              ) : null}
            </Box>
            <Typography variant='body2' color='text.secondary'>
              {`UPC #: ${upcLabel}`}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'cost',
      headerName: 'Cost',
      renderHeader: () => renderHelpHeader('Cost', 'Cost'),
      width: 120,
      renderCell: (params) => {
        const onSale = params.row.on_sale;
        let textColor = 'inherit';
        if (onSale) {
          textColor = green[700];
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1,
              borderRadius: 1,
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              variant='body1'
              sx={{ fontWeight: onSale ? 'bold' : 'normal', color: textColor }}
            >
              ${params.value}
            </Typography>
            {onSale && (
              <Tooltip title='On Sale!' placement='right'>
                <LocalOfferIcon sx={{ ml: 1, color: green[700] }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'item_number',
      headerName: 'SS Item #',
      width: 1,
      sortable: false,
    },
  ];
};

export default getColumns;
